import { LocationMapDialog } from '@/components/controls';
import { getGroupType, spin } from '@/utils';
import {
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { Masonry } from '@mui/lab';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { OldOccurrence } from './OldOccurrence';
import { useOldNicheOccurrences } from './useOldNicheOccurrences';

export function OldOccurrences({ title, groupCodes, onComplete }) {
  // const [days, setDays] = useState(28);
  const days = 28;
  const {
    query: { data: occurrences, isFetching, refetch },
  } = useOldNicheOccurrences(getGroupType('SECTOR'), groupCodes, days);
  const [selectedId, setSelectedId] = useState(null);
  const { width, ref } = useResizeDetector();
  const columns = useMemo(() => Math.floor(width / 248) || 1, [width]);
  const selectedOccurrence = useMemo(
    () => occurrences.find((instance) => instance.number === selectedId),
    [occurrences, selectedId],
  );

  function handleRefreshClick() {
    refetch();
  }

  function handleCompleteClick() {
    onComplete();
  }

  const handleShowMapClick = (id) => () => {
    setSelectedId(id);
  };

  function handleMapClose() {
    setSelectedId(null);
  }

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">{title}</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton onClick={handleRefreshClick} disabled={isFetching}>
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    isFetching
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ pb: 1 }} ref={ref}>
        <Masonry spacing={1} columns={columns}>
          {occurrences.map((occurrence) => (
            <OldOccurrence
              key={occurrence.number}
              occurrence={occurrence}
              onShowMapClick={handleShowMapClick(occurrence.number)}
            />
          ))}
        </Masonry>
      </Stack>
      {selectedOccurrence?.address && (
        <LocationMapDialog
          addresses={[selectedOccurrence.address]}
          title={`Points of interest for ${selectedOccurrence.number}`}
          isOpen={Boolean(selectedOccurrence)}
          onClose={handleMapClose}
        />
      )}
    </Box>
  );
}
